import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { FunctionModuleView } from "../api/access";
import { closure, requiresMap, type EffectiveEntry } from "../lib/effectiveAccess";
import { matchesQuery } from "../lib/foldText";
import Ic from "./icons.jsx";

/**
 * The function matrix shared by the role editor and the user-access screen
 * (sprint 7 §5): one card per module (title, description, "everything in
 * this module" switch), one row per function (name, description, key).
 *
 *  - `mode="role"`: a checkbox per function; `disabled` dims the whole matrix
 *    (the role has "all functions"). A function that a ticked one needs (§1.1)
 *    shows as ticked and locked, with the functions that bring it — it can
 *    only go once they do.
 *  - `mode="user"`: three states per row — "Iz uloge" (default, shows the
 *    effective ✓/— and which role gives it), "Dodato" (grant), "Oduzeto"
 *    (deny) — as a segmented button. Implied and cascade-denied functions
 *    say so in the badge.
 *
 * The search box filters by name / key / module without diacritics. Every
 * module and row stays mounted; non-matching ones are `hidden`, so the
 * container never jumps (blueprint 09 §3).
 */

/** Per-row state on the user screen: inherit from roles, or an explicit exception. */
export type UserRowState = "ROLE" | "GRANT" | "DENY";

type CommonProps = {
  modules: FunctionModuleView[];
  /** Read-only rendering (no `users:access`, or the role has all functions). */
  disabled?: boolean;
};

type RoleModeProps = CommonProps & {
  mode: "role";
  /** What the administrator ticked; what those need is derived and shown locked. */
  selected: ReadonlySet<string>;
  onChange: (next: Set<string>) => void;
};

type UserModeProps = CommonProps & {
  mode: "user";
  grantKeys: ReadonlySet<string>;
  denyKeys: ReadonlySet<string>;
  /** Live effective picture, keyed by function key (lib/effectiveAccess). */
  effective: ReadonlyMap<string, EffectiveEntry>;
  onChange: (keys: string[], state: UserRowState) => void;
};

export type FunctionMatrixProps = RoleModeProps | UserModeProps;

const NO_KEYS: ReadonlySet<string> = new Set();

export function FunctionMatrix(props: FunctionMatrixProps) {
  const { t } = useTranslation();
  const { modules, disabled } = props;
  const [query, setQuery] = useState("");

  const requires = useMemo(() => requiresMap(modules), [modules]);
  const nameByKey = useMemo(() => {
    const out = new Map<string, string>();
    for (const m of modules) for (const f of m.functions) out.set(f.key, f.name);
    return out;
  }, [modules]);
  const names = (keys: readonly string[]) => keys.map((k) => nameByKey.get(k) ?? k).join(", ");

  // Role mode: what the ticked functions bring along (§1.1) — ticked and locked in the matrix.
  const selected = props.mode === "role" ? props.selected : NO_KEYS;
  const implied = useMemo(() => {
    const out = new Set<string>();
    for (const k of closure(selected, requires)) if (!selected.has(k)) out.add(k);
    return out;
  }, [selected, requires]);
  const bringersOf = (key: string) => [...selected].filter((b) => b !== key && closure([b], requires).has(key));

  // Which rows match the query — computed once per keystroke, applied as `hidden`.
  const visible = useMemo(() => {
    const rows = new Set<string>();
    for (const m of modules) {
      for (const f of m.functions) {
        if (matchesQuery(`${m.moduleName} ${f.name} ${f.key} ${f.description ?? ""}`, query)) rows.add(f.key);
      }
    }
    return rows;
  }, [modules, query]);

  const anyVisible = modules.some((m) => m.functions.some((f) => visible.has(f.key)));

  return (
    <div className={`fn-matrix${disabled ? " is-disabled" : ""}`} aria-disabled={disabled || undefined}>
      <div className="filter-bar" style={{ marginBottom: 12 }}>
        <div className="search-wrap">
          <Ic.search />
          <input
            className="input"
            style={{ paddingLeft: 32 }}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("functions.searchPlaceholder", "Pretraga funkcija — naziv, ključ ili modul…")}
            aria-label={t("functions.search", "Pretraga funkcija")}
            disabled={disabled}
          />
        </div>
      </div>

      <div className="fn-modules">
        {modules.map((m) => {
          const keys = m.functions.map((f) => f.key);
          const moduleVisible = keys.some((k) => visible.has(k));
          return (
            <section key={m.module} className="card fn-module" hidden={!moduleVisible}>
              <div className="card-h">
                <div style={{ minWidth: 0 }}>
                  <h3>{m.moduleName}</h3>
                  {m.moduleDescription && <div className="sub">{m.moduleDescription}</div>}
                </div>
                <ModuleSwitch props={props} keys={keys} implied={implied} />
              </div>
              <div className="fn-rows">
                {m.functions.map((f) => {
                  const isImplied = props.mode === "role" && !props.selected.has(f.key) && implied.has(f.key);
                  return (
                    <div key={f.key} className={`fn-row${isImplied ? " is-implied" : ""}`} hidden={!visible.has(f.key)}>
                      {props.mode === "role" ? (
                        <label className="fn-row-main check-row" style={{ minHeight: 0 }}>
                          <input
                            type="checkbox"
                            checked={props.selected.has(f.key) || isImplied}
                            disabled={disabled || isImplied}
                            onChange={(e) => {
                              const next = new Set(props.selected);
                              if (e.target.checked) next.add(f.key); else next.delete(f.key);
                              props.onChange(next);
                            }}
                            aria-label={f.name}
                          />
                          <RowText
                            name={f.name}
                            description={f.description}
                            fnKey={f.key}
                            hint={isImplied ? t("functions.impliedBy", "Uključeno preko: {{names}}", { names: names(bringersOf(f.key)) }) : undefined}
                          />
                        </label>
                      ) : (
                        <>
                          <div className="fn-row-main">
                            <EffectiveBadge entry={props.effective.get(f.key)} names={names} />
                            <RowText name={f.name} description={f.description} fnKey={f.key} />
                          </div>
                          <RowStateSwitch
                            name={f.name}
                            value={rowState(props, f.key)}
                            disabled={disabled}
                            onChange={(s) => props.onChange([f.key], s)}
                          />
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          );
        })}
        <div className="card" hidden={anyVisible}>
          <div className="empty">{t("functions.noMatch", "Nijedna funkcija ne odgovara pretrazi.")}</div>
        </div>
      </div>
    </div>
  );
}

function rowState(p: UserModeProps, key: string): UserRowState {
  if (p.denyKeys.has(key)) return "DENY";
  if (p.grantKeys.has(key)) return "GRANT";
  return "ROLE";
}

function RowText({ name, description, fnKey, hint }: { name: string; description: string | null; fnKey: string; hint?: string }) {
  return (
    <div className="fn-row-text">
      <div className="fn-row-name">{name}</div>
      {description && <div className="fn-row-desc">{description}</div>}
      {hint && <div className="fn-row-hint">{hint}</div>}
      <code className="fn-key">{fnKey}</code>
    </div>
  );
}

/**
 * Effective ✓/— plus where it comes from ("iz uloge: Održavanje", "dodato",
 * "uključeno preko: Montaža…", "oduzeto", "isključeno — oduzeto: Pregled asseta", "nema").
 */
function EffectiveBadge({ entry, names }: { entry: EffectiveEntry | undefined; names: (keys: readonly string[]) => string }) {
  const { t } = useTranslation();
  const granted = entry?.granted ?? false;
  let source: string;
  switch (entry?.source) {
    case "ROLE":
      source = t("functions.effective.fromRole", "iz uloge: {{roles}}", { roles: entry.roleNames.join(", ") });
      break;
    case "GRANT":
      source = t("functions.effective.granted", "dodato");
      break;
    case "IMPLIED":
      source = t("functions.effective.implied", "uključeno preko: {{names}}", { names: names(entry.via) });
      break;
    case "DENY":
      source = entry.roleNames.length > 0
        ? t("functions.effective.deniedFromRole", "oduzeto (uloga: {{roles}})", { roles: entry.roleNames.join(", ") })
        : t("functions.effective.denied", "oduzeto");
      break;
    case "DENY_IMPLIED":
      source = t("functions.effective.deniedImplied", "isključeno — oduzeto: {{names}}", { names: names(entry.via) });
      break;
    default:
      source = t("functions.effective.none", "nema");
  }
  return (
    <div className="fn-eff" data-granted={granted ? "1" : "0"}>
      <span className={`tag ${granted ? "ok" : "neutral"}`} aria-label={granted ? t("functions.effective.yes", "Ima") : t("functions.effective.no", "Nema")}>
        {granted ? "✓" : "—"}
      </span>
      <span className="fn-eff-src">{source}</span>
    </div>
  );
}

/** Three-state segmented button: Iz uloge / Dodato / Oduzeto. */
function RowStateSwitch({ name, value, disabled, onChange, compact }: {
  name: string;
  value: UserRowState | null;
  disabled?: boolean;
  onChange: (s: UserRowState) => void;
  compact?: boolean;
}) {
  const { t } = useTranslation();
  const options: { value: UserRowState; label: string; cls: string }[] = [
    { value: "ROLE", label: t("functions.state.role", "Iz uloge"), cls: "" },
    { value: "GRANT", label: t("functions.state.grant", "Dodato"), cls: "on-ok" },
    { value: "DENY", label: t("functions.state.deny", "Oduzeto"), cls: "on-err" },
  ];
  return (
    <div className={`seg${compact ? " sm" : ""}`} role="group" aria-label={name}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          className={`${o.cls}${value === o.value ? " is-on" : ""}`.trim()}
          aria-pressed={value === o.value}
          disabled={disabled}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** Module header control: "everything in this module". */
function ModuleSwitch({ props, keys, implied }: { props: FunctionMatrixProps; keys: string[]; implied: ReadonlySet<string> }) {
  const { t } = useTranslation();
  if (props.mode === "role") {
    // Implied rows count as held: the module is "all" when nothing in it is still off.
    const count = keys.filter((k) => props.selected.has(k) || implied.has(k)).length;
    const all = count === keys.length && keys.length > 0;
    return (
      <label className="check-row fn-module-all" style={{ minHeight: 0 }}>
        <input
          type="checkbox"
          checked={all}
          ref={(el) => { if (el) el.indeterminate = !all && count > 0; }}
          disabled={props.disabled}
          onChange={(e) => {
            const next = new Set(props.selected);
            for (const k of keys) { if (e.target.checked) next.add(k); else next.delete(k); }
            props.onChange(next);
          }}
          aria-label={t("functions.moduleAllAria", "Sve u modulu")}
        />
        <span>{t("functions.moduleAll", "Sve u modulu")}</span>
        <span className="muted">{count}/{keys.length}</span>
      </label>
    );
  }
  // User mode: a shared state only when every row agrees; otherwise nothing is pressed.
  const states = new Set(keys.map((k) => rowState(props, k)));
  const shared = states.size === 1 ? [...states][0] : null;
  const granted = keys.filter((k) => props.effective.get(k)?.granted).length;
  return (
    <div className="fn-module-all">
      <span className="muted" style={{ fontSize: 12, whiteSpace: "nowrap" }}>
        {t("functions.moduleEffective", "{{granted}}/{{total}} ima", { granted, total: keys.length })}
      </span>
      <RowStateSwitch
        compact
        name={t("functions.moduleAllAria", "Sve u modulu")}
        value={shared}
        disabled={props.disabled}
        onChange={(s) => props.onChange(keys, s)}
      />
    </div>
  );
}
